export type SplitEmailBody = {
  history: string;
  message: string;
};

const QUOTE_START_SELECTOR = [
  '[id$="appendonsend"]',
  '[id$="divRplyFwdMsg"]',
  '#mail-editor-reference-message-container',
  '.gmail_quote',
  '.moz-cite-prefix',
].join(', ');

const CITE_SELECTOR = 'blockquote[type="cite" i]';

const OUTLOOK_HEADER_STYLE =
  /border:\s*none;\s*border-top:\s*solid\s+#(?:b5c4df|e1e1e1)\s+1(?:\.0)?pt/i;

const SKIPPED_ELEMENTS = new Set(['HEAD', 'META', 'SCRIPT', 'STYLE', 'TEMPLATE', 'TITLE']);
const VISIBLE_CHARACTER = /[^\s\u200b\u200c\u2060]/;

export const splitQuotedHistory = (rawHtml: string): SplitEmailBody => {
  const doc = new DOMParser().parseFromString(rawHtml, 'text/html');
  const { body } = doc;
  const unsplit = { history: '', message: body.innerHTML };

  const quoteStart = findQuoteStart(doc);
  if (!quoteStart) {
    return unsplit;
  }

  const beforeQuote = doc.createRange();
  beforeQuote.selectNodeContents(body);
  beforeQuote.setEndBefore(quoteStart);
  const message = cloneContents(doc, beforeQuote);
  trimTrailingBlankNodes(message);

  const fromQuote = doc.createRange();
  fromQuote.selectNodeContents(body);
  fromQuote.setStartBefore(quoteStart);
  const history = cloneContents(doc, fromQuote);

  if (!hasContent(message) || !hasContent(history)) {
    return unsplit;
  }

  return { history: history.innerHTML, message: message.innerHTML };
};

const findQuoteStart = (doc: Document): Element | undefined => {
  const trailingCite = findTrailingCite(doc);
  const candidates = doc.body.querySelectorAll(
    `${QUOTE_START_SELECTOR}, ${CITE_SELECTOR}, div[style]`,
  );

  return Array.from(candidates).find(
    element =>
      element === trailingCite ||
      ((element.matches(QUOTE_START_SELECTOR) ||
        OUTLOOK_HEADER_STYLE.test(element.getAttribute('style') ?? '')) &&
        !element.closest(CITE_SELECTOR)),
  );
};

const findTrailingCite = (doc: Document): Element | undefined => {
  const outermost = Array.from(doc.body.querySelectorAll(CITE_SELECTOR)).filter(
    cite => !cite.parentElement?.closest(CITE_SELECTOR),
  );
  if (outermost.length === 0) {
    return undefined;
  }

  let index = outermost.length - 1;
  while (index > 0 && !hasContentBetween(doc, outermost[index - 1], outermost[index])) {
    index -= 1;
  }
  return outermost[index];
};

const hasContentBetween = (doc: Document, first: Node, second: Node): boolean => {
  const range = doc.createRange();
  range.setStartAfter(first);
  range.setEndBefore(second);
  return hasContent(cloneContents(doc, range));
};

const cloneContents = (doc: Document, range: Range): HTMLElement => {
  const container = doc.createElement('div');
  container.append(range.cloneContents());
  return container;
};

const hasContent = (node: Node): boolean => {
  if (node.nodeType === Node.TEXT_NODE) {
    return VISIBLE_CHARACTER.test(node.textContent ?? '');
  }
  if (!(node instanceof Element) || SKIPPED_ELEMENTS.has(node.tagName)) {
    return false;
  }
  return node.tagName === 'IMG' || Array.from(node.childNodes).some(hasContent);
};

const trimTrailingBlankNodes = (element: Element) => {
  while (element.lastChild && !hasContent(element.lastChild)) {
    element.lastChild.remove();
  }
  const { lastChild } = element;
  if (lastChild instanceof Element && lastChild.tagName !== 'TABLE') {
    trimTrailingBlankNodes(lastChild);
  }
};
